import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { studentApi } from '@/api';
import { Button, Card, CardHeader, FileUploader, Icon, Skeleton, useToast } from '@/components/ui';
import { RESUME_ACCEPT, RESUME_HINT, RESUME_MAX_SIZE } from '@/features/resume/upload';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * The CV, from the profile page: which one is sent with applications, a place
 * to upload, and the way into filling the profile from it.
 *
 * Uploads through the same endpoint as the CV page, so a file added here is
 * on that page too. Managing several CVs stays on the CV page.
 */
export function ProfileCvCard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const { data, isPending } = useQuery({
    queryKey: ['student-resumes'],
    queryFn: studentApi.listResumes,
  });
  const resumes = data ?? [];
  const primary = resumes.find((resume) => resume.is_primary) ?? resumes[0];

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadResume(file),
    onSuccess: () => {
      toast.success('CV uploaded');
      ['student-resumes', 'student-dashboard', 'student-profile'].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (error) => toast.error(errorMessage(error, 'Upload failed')),
  });

  // Reading proposes; the review screen is where the student decides.
  const read = useMutation({
    mutationFn: (id) => studentApi.parseResume(id),
    onSuccess: (parse) => {
      queryClient.setQueryData(['resume-parse', parse.resume_id], parse);
      void queryClient.invalidateQueries({ queryKey: ['student-resumes'] });
      navigate(paths.student.cvReview(parse.resume_id));
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not read the file')),
  });

  return (
    <Card>
      <CardHeader
        title="CV"
        description="Sent with every application."
        action={
          resumes.length > 0 && (
            <Link to={paths.student.cv} className="text-brand-700 text-xs font-medium">
              Manage
            </Link>
          )
        }
      />
      <div className="space-y-3">
        {isPending ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          primary && (
            <div className="border-line rounded-input flex items-center gap-2.5 border px-3 py-2">
              <Icon name="fileText" size={18} className="text-brand-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-ink truncate text-sm font-medium" title={primary.file_name}>
                  {primary.file_name}
                </p>
                <p className="text-ink-muted text-xs">
                  {primary.is_primary ? 'Primary CV' : 'Your CV'}
                  {resumes.length > 1 && ` · ${resumes.length} on file`}
                </p>
              </div>
            </div>
          )
        )}

        {primary && primary.parse_status !== 'FAILED' && (
          <Button
            variant="tintBrand"
            size="sm"
            fullWidth
            isLoading={read.isPending}
            onClick={() =>
              primary.parse_status
                ? navigate(paths.student.cvReview(primary.id))
                : read.mutate(primary.id)
            }
          >
            <Icon name="userCheck" size={15} />
            {primary.parse_status ? 'Review profile suggestions' : 'Fill my profile from this CV'}
          </Button>
        )}

        <FileUploader
          accept={RESUME_ACCEPT}
          maxSizeBytes={RESUME_MAX_SIZE}
          isUploading={upload.isPending}
          hint={RESUME_HINT}
          onSelect={(file) => upload.mutate(file)}
        />
      </div>
    </Card>
  );
}
